import api from './api';
import type { ApiResponse } from '@/types';

/**
 * Logistics / Driver Routing — the dispatch board feed + driver/transfer CRUD.
 *
 * A coordinator-facing surface: every patient ride for ONE calendar day
 * (airport pickups, airport drop-offs, hotel↔clinic shuttles, custom errands)
 * laid out as one lane per driver plus an "unassigned" lane. The board is a
 * poll-driven snapshot (`GET /logistics/board?date=`), so no websocket:
 * `/logistics` reloads it on a 30s interval and after every mutation.
 *
 * Shape notes the UI leans on:
 *   - `unassigned` holds the non-cancelled driverless transfers, pickup_time
 *     NULLs FIRST (the auto-suggested rides that still need a time — the
 *     "needs scheduling" stack in the right rail).
 *   - `drivers` holds every ACTIVE driver in `sort_order`, PLUS retired drivers
 *     that still hold a ride on this date (so history never vanishes).
 *   - CANCELLED transfers ARE returned inside the lanes; the UI mutes and
 *     strikes them through rather than dropping them.
 *   - `pickup_time` arrives as 'HH:mm' OR 'HH:mm:ss' depending on the driver of
 *     the write (form vs. auto-generation) — always normalize for display.
 *   - `source: 'auto'` rides are generated from a travel record / appointment.
 *     DELETE 422s on them, so the UI hides delete and offers cancel instead.
 *
 * Permissions: read is `logistics.view`; every mutation is `logistics.manage`.
 * The whole route group sits behind `feature:logistics_enabled` (404 when off).
 *
 * Types are declared HERE (not in `@/types`) so the module is self-contained —
 * same convention as `salla-board-api.ts` / `rooms-api.ts`.
 */

/** What kind of ride this is — drives the icon, tint and flight-field visibility. */
export type TransferKind =
  | 'arrival_pickup'
  | 'departure_dropoff'
  | 'clinic_ride'
  | 'custom';

/** Lifecycle of a single ride. */
export type TransferStatus =
  | 'unassigned'
  | 'assigned'
  | 'completed'
  | 'no_show'
  | 'cancelled';

/** Who created the ride — `auto` rides are derived and cannot be deleted. */
export type TransferSource = 'auto' | 'manual';

/** The airport kinds — the only ones that carry a flight number. */
export const AIRPORT_KINDS: TransferKind[] = [
  'arrival_pickup',
  'departure_dropoff',
];

/** Minimal patient reference embedded on a transfer (never the full lead). */
export interface TransferLead {
  id: string;
  full_name: string;
  phone: string;
}

/** Minimal driver reference embedded on a transfer. */
export interface TransferDriverRef {
  id: string;
  name: string;
}

/** A driver record. Roster row first; `user_id` gives it a login on clinics
 *  running the pull model (ArtDent 260904), and is null everywhere else. */
export interface Driver {
  id: string;
  name: string;
  phone: string | null;
  /** Free-text plate / model, e.g. "Passat · AA123BB". */
  vehicle: string | null;
  /** Seats; null when unknown (the UI never hard-blocks on it). */
  capacity: number | null;
  notes: string | null;
  sort_order: number;
  /** False once soft-retired — kept on past rides, never offered for new ones. */
  is_active: boolean;
  /**
   * The OPTIONAL login (ArtDent 260904). Null on every clinic running the push
   * board — a driver without an account is still a complete roster row — and
   * set on clinics where the driver claims and reports their own runs.
   */
  user_id?: number | null;
  user?: { id: number; name: string; email: string } | null;
  /**
   * The standing shift (ArtDent 260924). Null = no shift, which means the
   * driver covers every hour. Only served meaningfully where
   * `logistics_shift_routing_enabled` is on.
   */
  driver_shift_id?: string | null;
  shift?: DriverShift | null;
}

/**
 * A driver's standing shift, e.g. "Day 09:00–17:00" or "Night 17:00–09:00"
 * (ArtDent 260924). `ends_at` before `starts_at` is the overnight shape, and the
 * server says so in `is_overnight` so every screen agrees.
 */
export interface DriverShift {
  id: string;
  name: string;
  /** 'HH:mm', clinic wall clock. */
  starts_at: string;
  ends_at: string;
  is_overnight: boolean;
  sort_order: number;
  /** Present on the settings list only. */
  drivers_count?: number;
}

export interface DriverShiftData {
  name: string;
  starts_at: string;
  ends_at: string;
}

/** Staff who could be handed a driver's login. */
export interface LinkableDriverUser {
  id: number;
  name: string;
  email: string;
}

/** One ride on the board. */
export interface Transfer {
  id: string;
  kind: TransferKind;
  status: TransferStatus;
  source: TransferSource;
  /** Local calendar day, `YYYY-MM-DD` (never an ISO instant). */
  pickup_date: string;
  /** 'HH:mm' or 'HH:mm:ss'; null = "needs scheduling". */
  pickup_time: string | null;
  pickup_location: string | null;
  dropoff_location: string | null;
  flight_number: string | null;
  /**
   * ArtDent 261015 — when the plane lands (arrival leg) or takes off
   * (departure leg), 'HH:mm', from the ride's trip. The pickup runs a clinic
   * lead ahead of it, so the driver sheets print both. Board payload only;
   * null on clinic/custom rides and on trips without the time.
   */
  flight_time?: string | null;
  passenger_count: number;
  driver_id: string | null;
  notes: string | null;
  /** Source travel record, when the ride was derived from one. */
  travel_id: string | null;
  /** Source appointment, for clinic rides derived from a booking. */
  appointment_id: string | null;
  lead: TransferLead | null;
  driver: TransferDriverRef | null;
  /**
   * ArtDent 260904 — the pull model. The office may PROPOSE a run to a driver,
   * but it is not theirs until they confirm: the run stays `unassigned` with no
   * `driver`, and this says who is being waited on. Absent on clinics that run
   * the push board, where assigning hands the ride over outright.
   */
  offered_to?: TransferDriverRef | null;
  offered_at?: string | null;
  /**
   * How the ride got its driver (ArtDent 260924): `shift` = placed by the
   * driver's shift, `manual` = the office chose, `taken` = a driver took it.
   * Null when nobody has decided yet.
   */
  assignment_source?: 'shift' | 'manual' | 'taken' | null;
  /** The car this ride shares with other patients, if any (ArtDent 260924). */
  group_id?: string | null;
  /**
   * The last thing a driver reported on this run — what a coordinator glances
   * at. The full trail lives on the driver's own surface.
   */
  last_event?: {
    event: string;
    location: string | null;
    occurred_at: string | null;
  } | null;
}

/** One driver's lane on the board (driver + that day's rides, time-ordered). */
export interface LogisticsBoardLane {
  driver: Driver;
  transfers: Transfer[];
}

/** Header counters for the stats strip. */
export interface LogisticsBoardSummary {
  total: number;
  unassigned: number;
  assigned: number;
  completed: number;
  cancelled: number;
  arrivals: number;
  departures: number;
  clinic_rides: number;
  custom: number;
}

/** `GET /logistics/board?date=YYYY-MM-DD` — the whole day in one payload. */
export interface LogisticsBoard {
  /** Echo of the requested day, `YYYY-MM-DD`. */
  date: string;
  /** Driverless, non-cancelled rides — pickup_time NULLs first. */
  unassigned: Transfer[];
  /** Active drivers (in sort_order) + retired drivers holding a ride today. */
  drivers: LogisticsBoardLane[];
  summary: LogisticsBoardSummary;
}

/** Server-side filters for the flat transfer list. */
export interface TransferFilters {
  date?: string;
  driver_id?: string;
  status?: TransferStatus;
  kind?: TransferKind;
}

export interface CreateTransferData {
  kind: TransferKind;
  pickup_date: string;
  pickup_time?: string | null;
  /** Required unless `kind === 'custom'` (a ride with no patient). */
  lead_id?: string | null;
  appointment_id?: string | null;
  driver_id?: string | null;
  pickup_location?: string | null;
  dropoff_location?: string | null;
  flight_number?: string | null;
  passenger_count?: number;
  notes?: string | null;
}

export type UpdateTransferData = Partial<CreateTransferData>;

export interface CreateDriverData {
  name: string;
  /** Optional login — see Driver.user_id. */
  user_id?: number | null;
  phone?: string | null;
  vehicle?: string | null;
  capacity?: number | null;
  notes?: string | null;
  sort_order?: number;
  is_active?: boolean;
  /** Standing shift id, or null for none — see Driver.driver_shift_id. */
  driver_shift_id?: string | null;
}

export type UpdateDriverData = Partial<CreateDriverData>;

/* ------------------------------------------------------------------ board */

export const getLogisticsBoard = async (date: string): Promise<LogisticsBoard> => {
  const response = await api.get<ApiResponse<LogisticsBoard>>(
    `/logistics/board?date=${encodeURIComponent(date)}`,
  );
  return response.data.data;
};

/* ---------------------------------------------------------------- drivers */

/** ALL drivers, active + retired, ordered by sort_order. */
export const getDrivers = async (): Promise<Driver[]> => {
  const response = await api.get<ApiResponse<Driver[]>>('/logistics/drivers');
  return response.data.data;
};

/**
 * Staff the roster screen may offer as a driver's login.
 *
 * Deliberately NOT /users: a coordinator manages the roster without necessarily
 * holding `users.view`, and this must never become a staff directory — the
 * endpoint returns only active holders of `logistics.drive`. Pass the driver
 * being edited so its own login stays in the list.
 */
export const getLinkableDriverUsers = async (
  driverId?: string,
): Promise<LinkableDriverUser[]> => {
  const query = driverId ? `?driver=${encodeURIComponent(driverId)}` : '';
  const response = await api.get<ApiResponse<LinkableDriverUser[]>>(
    `/logistics/drivers/linkable-users${query}`,
  );
  return response.data.data;
};

export const createDriver = async (data: CreateDriverData): Promise<Driver> => {
  const response = await api.post<ApiResponse<Driver>>('/logistics/drivers', data);
  return response.data.data;
};

export const updateDriver = async (
  id: string,
  data: UpdateDriverData,
): Promise<Driver> => {
  const response = await api.put<ApiResponse<Driver>>(`/logistics/drivers/${id}`, data);
  return response.data.data;
};

/** Soft-retire (is_active = false) — drivers are never hard-deleted. */
export const deleteDriver = async (id: string): Promise<void> => {
  await api.delete(`/logistics/drivers/${id}`);
};

/** POST .../reorder returns only a message — refetch to read the new order. */
export const reorderDrivers = async (orderedIds: string[]): Promise<Driver[]> => {
  await api.post('/logistics/drivers/reorder', { ids: orderedIds });
  return getDrivers();
};

/* ---------------------------------------------------------- driver shifts */

/**
 * The standing shift list (ArtDent 260924). 404s unless the clinic runs
 * `logistics_shift_routing_enabled` — callers gate on the flag first.
 */
export const getDriverShifts = async (): Promise<DriverShift[]> => {
  const response = await api.get<ApiResponse<DriverShift[]>>('/logistics/driver-shifts');
  return response.data.data;
};

export const createDriverShift = async (data: DriverShiftData): Promise<DriverShift> => {
  const response = await api.post<ApiResponse<DriverShift>>('/logistics/driver-shifts', data);
  return response.data.data;
};

export const updateDriverShift = async (
  id: string,
  data: DriverShiftData,
): Promise<DriverShift> => {
  const response = await api.put<ApiResponse<DriverShift>>(`/logistics/driver-shifts/${id}`, data);
  return response.data.data;
};

/** A real delete — its drivers are left with no shift (they cover every hour). */
export const deleteDriverShift = async (id: string): Promise<void> => {
  await api.delete(`/logistics/driver-shifts/${id}`);
};

/* -------------------------------------------------------------- transfers */

export const getTransfers = async (
  filters: TransferFilters = {},
): Promise<Transfer[]> => {
  const params = new URLSearchParams();
  if (filters.date) params.set('date', filters.date);
  if (filters.driver_id) params.set('driver_id', filters.driver_id);
  if (filters.status) params.set('status', filters.status);
  if (filters.kind) params.set('kind', filters.kind);
  const query = params.toString();
  const response = await api.get<ApiResponse<Transfer[]>>(
    `/logistics/transfers${query ? `?${query}` : ''}`,
  );
  return response.data.data;
};

export const createTransfer = async (
  data: CreateTransferData,
): Promise<Transfer> => {
  const response = await api.post<ApiResponse<Transfer>>('/logistics/transfers', data);
  return response.data.data;
};

/**
 * A trip's own arrival pickup / departure drop-off — created, revived from
 * cancelled, or returned as is. Use this, never `createTransfer`, for a trip's
 * ride: a manual ride carries no `travel_id`, so the trip would never see it.
 */
export const ensureTravelLeg = async (
  travelId: string,
  kind: 'arrival_pickup' | 'departure_dropoff',
): Promise<Transfer> => {
  const response = await api.post<ApiResponse<Transfer>>(
    `/logistics/travels/${travelId}/legs`,
    { kind },
  );
  return response.data.data;
};

export interface FlightCheckIn {
  travel_id: string;
  leg: 'arrival' | 'departure';
  checked_in: boolean;
  checked_in_at: string | null;
}

/**
 * Radiance 261001 — the patient is (or is not) checked in for one of the
 * trip's flights. Yes stamps who and when; No clears it.
 */
export const setFlightCheckIn = async (
  travelId: string,
  leg: 'arrival' | 'departure',
  checkedIn: boolean,
): Promise<FlightCheckIn> => {
  const response = await api.patch<ApiResponse<FlightCheckIn>>(
    `/logistics/travels/${travelId}/check-in`,
    { leg, checked_in: checkedIn },
  );
  return response.data.data;
};

export const updateTransfer = async (
  id: string,
  data: UpdateTransferData,
): Promise<Transfer> => {
  const response = await api.put<ApiResponse<Transfer>>(
    `/logistics/transfers/${id}`,
    data,
  );
  return response.data.data;
};

/** 422s for `source === 'auto'` rides — cancel those instead of deleting. */
export const deleteTransfer = async (id: string): Promise<void> => {
  await api.delete(`/logistics/transfers/${id}`);
};

/**
 * Assign / re-assign / unassign (driver_id: null) a ride, optionally setting the
 * pickup time in the same call — the assign popover requires a time when the
 * ride has none, so the coordinator never leaves a scheduled ride timeless.
 */
export const assignTransfer = async (
  id: string,
  payload: { driver_id: string | null; pickup_time?: string },
): Promise<Transfer> => {
  const response = await api.patch<ApiResponse<Transfer>>(
    `/logistics/transfers/${id}/assign`,
    payload,
  );
  return response.data.data;
};

export const setTransferStatus = async (
  id: string,
  status: TransferStatus,
): Promise<Transfer> => {
  const response = await api.patch<ApiResponse<Transfer>>(
    `/logistics/transfers/${id}/status`,
    { status },
  );
  return response.data.data;
};

/* --------------------------------------------- the office plans a car */

/**
 * Several patients in one car, planned from the board (Dentour 260925). The
 * push-board twin of the driver's own run-groups; behind
 * `logistics_shift_routing_enabled` + `logistics.manage`. The board refetches
 * after each call, so only `dissolved` is read from the answer.
 */
export interface TransferCarResult {
  data: {
    id: string;
    driver_id: string;
    seats_taken: number;
    capacity: number | null;
    transfer_ids: string[];
  } | null;
  meta: { dissolved: boolean };
}

export const createTransferCar = async (
  driverId: string,
  transferIds: string[],
): Promise<TransferCarResult> => {
  const response = await api.post<TransferCarResult>('/logistics/transfer-groups', {
    driver_id: driverId,
    transfer_ids: transferIds,
  });
  return response.data;
};

export const addToTransferCar = async (
  groupId: string,
  transferIds: string[],
): Promise<TransferCarResult> => {
  const response = await api.post<TransferCarResult>(
    `/logistics/transfer-groups/${groupId}/transfers`,
    { transfer_ids: transferIds },
  );
  return response.data;
};

export const removeFromTransferCar = async (
  groupId: string,
  transferId: string,
): Promise<TransferCarResult> => {
  const response = await api.delete<TransferCarResult>(
    `/logistics/transfer-groups/${groupId}/transfers/${transferId}`,
  );
  return response.data;
};

export const splitTransferCar = async (groupId: string): Promise<void> => {
  await api.delete(`/logistics/transfer-groups/${groupId}`);
};

export const completeTransferCar = async (groupId: string): Promise<number> => {
  const response = await api.patch<{ data: { completed: number } }>(
    `/logistics/transfer-groups/${groupId}/complete`,
  );
  return response.data.data.completed;
};

/* --------------------------------------------- ride from an appointment */

/**
 * The transfer plus whether it was already there (260907).
 *
 * `already_sent` is not an error state — the endpoint is idempotent per
 * appointment, so a second press returns the run that exists rather than a
 * second one. The UI needs to tell the two apart only to pick a toast.
 */
export interface AppointmentRideResult {
  transfer: Transfer;
  alreadySent: boolean;
}

/**
 * POST /appointments/{id}/ride — send this patient's clinic ride to the drivers.
 *
 * Everything the ride needs is derived server-side from the appointment (the
 * patient, the day, a pickup 30 minutes before the slot, their hotel from the
 * trip covering that date, the clinic as the destination), so this takes no
 * body. It lands the run in the OPEN POOL: driverless, unassigned, offered to
 * nobody — which is what puts it on the dashboards of the drivers whose
 * rostered hours cover its pickup.
 *
 * 404 when the clinic does not run the pull model (`logistics_claim_enabled`);
 * 403 without `logistics.manage`; 422 when the appointment has no patient, is
 * not a clinic visit, or has been cancelled.
 */
export type AppointmentRideDirection = 'to_clinic' | 'to_hotel';

export const sendAppointmentRideToDrivers = async (
  appointmentId: string,
  // ArtDent 261015 — the board's "Needs travel" row sends the return leg too.
  direction: AppointmentRideDirection = 'to_clinic',
): Promise<AppointmentRideResult> => {
  const response = await api.post<ApiResponse<Transfer> & { meta?: { already_sent?: boolean } }>(
    `/appointments/${appointmentId}/ride`,
    { direction },
  );

  return {
    transfer: response.data.data,
    alreadySent: response.data.meta?.already_sent === true,
  };
};

/** Limited roster read for dispatchers, scoped server-side to this driver. */
export interface DriverWorkingHoursData {
  from: string;
  assignments: { date: string; shift_template_id: string }[];
  templates: {
    id: string;
    name: string;
    starts_at: string;
    ends_at: string;
    is_active: boolean;
  }[];
}

export const getDriverWorkingHours = async (driverId: string): Promise<DriverWorkingHoursData> => {
  const response = await api.get<ApiResponse<DriverWorkingHoursData>>(
    `/logistics/drivers/${driverId}/working-hours`,
  );
  return response.data.data;
};
